import { Copy, Link2, MoreVertical, RotateCcw } from "lucide-react";

import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItemLabel,
  MenuPopover,
  MenuTrigger,
} from "@/components/menu";

import type { ColumnConfig } from "../../types/tables";

export type RowActionKey = "copyLink" | "duplicate" | "reopen";

const ACTION_CONFIG: Record<
  RowActionKey,
  { icon: typeof Copy; label: string }
> = {
  copyLink: { icon: Link2, label: "Copy link" },
  duplicate: { icon: Copy, label: "Duplicate request" },
  reopen: { icon: RotateCcw, label: "Reopen" },
};

// Kebab-menu "Action" column shared across tabs, each with its own action set.
export function createActionsColumn<T extends { id: number }>(
  actions: RowActionKey[],
): ColumnConfig<T> {
  return {
    accessor: "id",
    cellRenderer: (_value, row, customProps) => (
      <MenuTrigger>
        <MenuButton
          className="text-text-tertiary hover:text-text-secondary p-2"
          noIndicator
          variant="tertiary"
        >
          <MoreVertical size={16} />
        </MenuButton>
        <MenuPopover>
          <Menu
            onAction={(key) => {
              if (key === "duplicate" && customProps?.onDuplicate) {
                (customProps.onDuplicate as (id: number) => void)(row.id);
              } else if (key === "copyLink" && customProps?.onCopyLink) {
                (customProps.onCopyLink as (id: number) => void)(row.id);
              } else if (key === "reopen" && customProps?.onReopen) {
                (customProps.onReopen as (id: number) => void)(row.id);
              }
            }}
          >
            {actions.map((actionKey) => {
              const { icon: Icon, label } = ACTION_CONFIG[actionKey];

              return (
                <MenuItem id={actionKey} key={actionKey}>
                  <Icon data-ui="icon" size={16} />
                  <MenuItemLabel>{label}</MenuItemLabel>
                </MenuItem>
              );
            })}
          </Menu>
        </MenuPopover>
      </MenuTrigger>
    ),
    header: "Action",
    key: "action",
    sortable: false,
    width: 80,
  };
}
